import { NgClass, NgForOf, NgIf } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CurrencyMaskModule } from 'ng2-currency-mask';
import { Posto } from 'src/app/rede-especial/seletor-postos/seletor-postos.component';
import { TipoAcordoEnum } from '../rede-especial-enuns';
import { BuscarPrecosMacroPostosItem } from '../rede-especial.interface';
import { RedeEspecialService } from '../rede-especial.service';

@Component({
  selector: 'app-confirmar-desconto-acrescimo',
  templateUrl: './confirmar-desconto-acrescimo.component.html',
  styleUrls: ['./confirmar-desconto-acrescimo.component.scss'],
  standalone: true,
  imports: [FormsModule, CurrencyMaskModule, NgForOf, NgIf, NgClass],
})
export class ConfirmarDescontoAcrescimoComponent implements OnChanges, OnInit {
  @Input() postosSelecionados: Posto[] = [];
  @Input() indCombustivel!: string;
  @Input() precoFinalSugerido!: number;
  @Input() tipoAcordoEnum!: number;
  @Output() postoRemovido = new EventEmitter<number>();
  @Output() precoFinalSugeridoAlterado = new EventEmitter<number>();

  // Propriedade local para o preço editável
  precoFinalLocal: number = 0;

  precos: BuscarPrecosMacroPostosItem[] = [];
  postosSemPreco: Posto[] = [];
  private service = inject(RedeEspecialService);
  private readonly diferecaMaximaPermitida: number = 4.0;
  private readonly mensagemDesconto =
    ' Atenção: Os acordos não serão criados para os seguintes postos, devido ao preço sugerido ser maior que o preço bomba ou desconto/acrescimo acima da diferença máxima permitida que é de R$ ';
  private readonly mensagemAcrescimo =
    ' Atenção: Os acordos não serão criados para os seguintes postos, devido ao preço sugerido ser menor que o preço bomba ou desconto/acrescimo acima da diferença máxima permitida que é de R$ ';

  @Output() confirmarDescontos = new EventEmitter<
    BuscarPrecosMacroPostosItem[]
  >();
  @Output() cancelarConfirmacao = new EventEmitter<void>();

  public invalidPrecoSugerido: number[] = [];
  public msgPrecoSugeridoInvalido =
    this.mensagemDesconto + this.diferecaMaximaPermitida;

  ngOnInit(): void {
    // Inicializa o valor local
    this.precoFinalLocal = this.precoFinalSugerido || 0;
  }

  ngOnChanges(): void {
    // Sincroniza o valor local com o input
    if (this.precoFinalSugerido !== undefined) {
      this.precoFinalLocal = this.precoFinalSugerido;
    }

    if (this.deveBuscarPrecos()) {
      this.pegarPrecosDosPostosSelecionados();
    }
    this.recalcularPrecos();
  }

  private deveBuscarPrecos(): boolean {
    return Boolean(this.postosSelecionados?.length && this.indCombustivel);
  }

  onPrecoFinalChange(): void {
    console.log('Preço alterado para:', this.precoFinalLocal);
    this.recalcularPrecos();
  }

  recalcularPrecos(): void {
    if (!this.temPrecos()) return;

    this.precos = this.precos.map((preco) =>
      this.mapearPrecoRecalculado(preco),
    );

    this.validarPrecoSugerido();
  }

  private filtrarPostosComPreco(postosOriginal: Posto[]): void {
    const codPostosComPreco = this.precos.map((p) => p.codPosto);

    this.postosSemPreco = postosOriginal.filter(
      (p) => !codPostosComPreco.includes(p.codPosto),
    );

    this.postosSelecionados = postosOriginal.filter((p) =>
      codPostosComPreco.includes(p.codPosto),
    );
  }

  private pegarPrecosDosPostosSelecionados(): void {
    const payload = {
      codsPostos: this.postosSelecionados.map((p) => p.codPosto).join(','),
      indCombustivel: this.indCombustivel,
    };

    this.service.getPrecosPostosMacroPostos(payload).subscribe((res) => {
      this.precos = res.retorno;
      this.filtrarPostosComPreco(this.postosSelecionados);
      this.recalcularPrecos();
    });
  }

  private arredondar2(valor: number): number {
    return Math.round(valor * 100) / 100;
  }

  private temPrecos(): boolean {
    return Boolean(this.precos?.length);
  }

  private obterValorSugerido(): number {
    return Number(this.precoFinalLocal) || 0;
  }

  private mapearPrecoRecalculado(
    preco: BuscarPrecosMacroPostosItem,
  ): BuscarPrecosMacroPostosItem {
    const precoFinal = this.precoFinalLocal;
    const descontoAcrescimo = this.calcularDescontoAcrescimo(
      preco.precoUnitario,
      precoFinal,
    );

    return {
      ...preco,
      precoUnitario: this.arredondar2(preco.precoUnitario),
      precoFinal: this.arredondar2(precoFinal),
      descontoAcrescimo: this.arredondar2(descontoAcrescimo),
    };
  }

  private garantirValorNaoNegativo(valor: number): number {
    return valor < 0 ? valor * -1 : valor;
  }

  private calcularDescontoAcrescimo(
    precoBomba: number,
    precoFinalSugerido: number,
  ): number {
    const tipoAcordoNumerico = Number(this.tipoAcordoEnum);

    console.log('Calculando:', {
      precoBomba,
      precoFinalSugerido,
      tipoAcordo: tipoAcordoNumerico,
      TipoEnum: TipoAcordoEnum,
    });

    if (tipoAcordoNumerico === TipoAcordoEnum.DESCONTO) {
      // Para desconto: precoBomba - precoFinal (positivo quando há desconto)
      // Ex: PrecoBomba R$5,00, PrecoFinal R$4,50 = Desconto de R$0,50
      const resultado = precoBomba - precoFinalSugerido;
      console.log('Resultado DESCONTO:', resultado);
      return resultado;
    } else if (tipoAcordoNumerico === TipoAcordoEnum.ACRESCIMO) {
      // Para acréscimo: precoFinal - precoBomba (positivo quando há acréscimo)
      // Ex: PrecoBomba R$5,00, PrecoFinal R$5,50 = Acréscimo de R$0,50
      const resultado = precoFinalSugerido - precoBomba;
      console.log('Resultado ACRÉSCIMO:', resultado);
      return resultado;
    }
    console.log('Tipo não reconhecido, retornando 0');
    return 0;
  }

  private calcularDiferencaPrecoBombaESugerido(
    precoBomba: number,
    precoFinalSugerido: number,
  ): number {
    return this.garantirValorNaoNegativo(precoBomba - precoFinalSugerido);
  }

  removerPostoDaLista(codPosto: number): void {
    if (!this.temPrecos()) return;
    this.precos = this.precos.filter((p) => p.codPosto !== codPosto);
    this.postoRemovido.emit(codPosto);
    this.recalcularPrecos();
  }

  getValorAbsoluto(valor: number | undefined | null): number {
    return valor == null ? 0 : Math.abs(valor);
  }

  validarPrecoSugerido(): void {
    this.invalidPrecoSugerido = [];
    if (!this.temPrecos()) return;

    const tipoAcordoNumerico = Number(this.tipoAcordoEnum);

    if (tipoAcordoNumerico === TipoAcordoEnum.DESCONTO) {
      this.validarPrecosDesconto();
      return;
    }

    if (tipoAcordoNumerico === TipoAcordoEnum.ACRESCIMO) {
      this.validarPrecosAcrescimo();
    }
  }

  private validarPrecosDesconto(): void {
    this.msgPrecoSugeridoInvalido =
      this.mensagemDesconto + this.diferecaMaximaPermitida;

    this.precos.forEach((item) => {
      const precoSugeridoInvalido = this.precoFinalLocal >= item.precoUnitario;
      const diferencaAcimaDoPermitido = this.excedeDiferencaMaxima(item);

      if (precoSugeridoInvalido || diferencaAcimaDoPermitido) {
        this.invalidPrecoSugerido.push(item.codPosto);
      }
    });
  }

  private validarPrecosAcrescimo(): void {
    this.msgPrecoSugeridoInvalido =
      this.mensagemAcrescimo + this.diferecaMaximaPermitida;

    this.precos.forEach((item) => {
      const precoSugeridoInvalido = this.precoFinalLocal <= item.precoUnitario;
      const diferencaAcimaDoPermitido = this.excedeDiferencaMaxima(item);

      if (precoSugeridoInvalido || diferencaAcimaDoPermitido) {
        this.invalidPrecoSugerido.push(item.codPosto);
      }
    });
  }

  private excedeDiferencaMaxima(item: BuscarPrecosMacroPostosItem): boolean {
    const valorAbsoluto = this.getValorAbsoluto(item.descontoAcrescimo);
    return valorAbsoluto > this.diferecaMaximaPermitida;
  }

  isPrecoSugeridoInvalido(item: BuscarPrecosMacroPostosItem): boolean {
    return this.invalidPrecoSugerido.includes(item.codPosto);
  }

  protected valorNegativo(
    descontoAcrescimo: number | null | undefined,
  ): boolean {
    if (descontoAcrescimo == null) return false;

    const tipoAcordoNumerico = Number(this.tipoAcordoEnum);

    // Para desconto: valor positivo = desconto aplicado, valor negativo = erro/acréscimo indevido
    if (tipoAcordoNumerico === TipoAcordoEnum.DESCONTO) {
      return descontoAcrescimo < 0;
    }

    // Para acréscimo: valor positivo = acréscimo aplicado, valor negativo = erro/desconto indevido
    if (tipoAcordoNumerico === TipoAcordoEnum.ACRESCIMO) {
      return descontoAcrescimo < 0;
    }

    return false;
  }
}
